import { useState } from "react";

import type { ColumnDef } from "@tanstack/react-table";

import type { components } from "../../api/schema";
import { docUrl } from "../../lib/docLinks";
import {
  Badge,
  Button,
  DataTable,
  HelpTip,
  Inline,
  InlineError,
  Select,
  Stack,
  Text,
} from "../../ui";
import { DateCell } from "../shared/DateCell";

type AlertRulePreview = components["schemas"]["AlertRulePreviewResponse"];
type AlertRulePreviewSample = components["schemas"]["AlertRulePreviewSample"];

const sourceColumns: Array<ColumnDef<AlertRulePreviewSample>> = [
  {
    header: "Evaluated",
    accessorKey: "evaluated_at",
    cell: ({ row }) => <DateCell value={row.original.evaluated_at} />,
  },
  {
    header: "Value",
    accessorKey: "value",
    cell: ({ row }) =>
      row.original.value == null ? "no value" : row.original.value.toFixed(4),
  },
  {
    header: "Occurrences",
    accessorKey: "occurrences",
    cell: ({ row }) => row.original.occurrences ?? "none",
  },
];

const evaluatedColumns: Array<ColumnDef<AlertRulePreviewSample>> = [
  sourceColumns[0] as ColumnDef<AlertRulePreviewSample>,
  {
    header: "Evaluation",
    id: "would_fire",
    accessorFn: (sample) => (sample.would_fire ? "firing" : "ok"),
    cell: ({ row }) => (
      <Badge tone={row.original.would_fire ? "danger" : "neutral"}>
        {row.original.would_fire ? "Would fire" : "OK"}
      </Badge>
    ),
  },
];

export interface AlertRuleBacktestProps {
  errorMessage?: string;
  isPending: boolean;
  onRun: () => void;
  preview?: AlertRulePreview;
}

export function AlertRuleBacktest({
  errorMessage,
  isPending,
  onRun,
  preview,
}: AlertRuleBacktestProps) {
  const [mode, setMode] = useState<"evaluated" | "source">("evaluated");
  const samples = preview?.samples ?? [];

  return (
    <Stack gap={3}>
      <Inline gap={3}>
        <Button
          disabled={isPending}
          onClick={onRun}
          type="button"
          variant="secondary"
        >
          {isPending ? "Running backtest..." : "Run recent backtest"}
        </Button>
        <HelpTip
          docHref={docUrl("concepts/alert-rules")}
          docLabel="Alert rules"
          label="About the alert backtest"
        >
          {"Replays a recent window at the live cadence."}
        </HelpTip>
        <Select
          label="Preview data"
          labelHidden
          size="sm"
          value={mode}
          onChange={(event) => {
            setMode(event.currentTarget.value as "evaluated" | "source");
          }}
        >
          <option value="source">Source data</option>
          <option value="evaluated">Evaluated data</option>
        </Select>
      </Inline>
      {errorMessage ? <InlineError>{errorMessage}</InlineError> : null}
      {preview ? (
        <>
          <Text aria-live="polite">
            This rule would have fired {preview.fire_count.toLocaleString()}{" "}
            {preview.fire_count === 1 ? "time" : "times"} in the recent backtest
            window.
          </Text>
          {samples.length > 0 ? (
            <DataTable
              ariaLabel={`${mode} alert backtest data`}
              columns={mode === "source" ? sourceColumns : evaluatedColumns}
              data={samples}
              getRowId={(sample) => sample.evaluated_at}
            />
          ) : (
            <Text variant="muted">No samples in this range.</Text>
          )}
        </>
      ) : null}
    </Stack>
  );
}
